import { ElementType, MouseEvent as ReactMouseEvent, memo } from 'react';
import { Box, Flex } from '@chakra-ui/react';

import { accent, font, neutral, radius, text } from 'styles/revampTokens';

import { BlockNavigationDropdownItem } from './BlockNavigationDropdown';
import { AVATAR_TINTS, hashIndex, initialsOf } from './dashboardNavDropdownShared';
import DashboardNavigationPageRow from './DashboardNavigationPageRow';

import type { DashboardNavPack } from './dashboardNavDropdownShared';

const DropdownBox = Box as ElementType;
const DropdownFlex = Flex as ElementType;

const ExpandChevronIcon = ({ expanded }: { expanded: boolean }) => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    style={{
      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
      transition: 'transform 0.15s ease',
    }}
  >
    <path
      d='M6 4l4 4-4 4'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

interface DashboardNavigationPackRowProperties {
  pack: DashboardNavPack;
  packPages: BlockNavigationDropdownItem[];
  isActivePack: boolean;
  isExpanded: boolean;
  isLoadingPages: boolean;
  previewPageId?: string | number | null;
  onToggleExpand: (pack: DashboardNavPack, event: ReactMouseEvent) => void;
  onDashboardOpen: (dashboardId: string) => void;
  onPageHover: (pageId: string | number) => void;
  onPageClick: (
    page: BlockNavigationDropdownItem,
    options?: import('./BlockNavigationDropdown').BlockNavigationClickOptions,
  ) => void;
}

const DashboardNavigationPackRow = memo(
  ({
    pack,
    packPages,
    isActivePack,
    isExpanded,
    isLoadingPages,
    previewPageId,
    onToggleExpand,
    onDashboardOpen,
    onPageHover,
    onPageClick,
  }: DashboardNavigationPackRowProperties) => {
    const tint = AVATAR_TINTS[hashIndex(pack.title, AVATAR_TINTS.length)];
    const pageCount = packPages.length > 0 ? packPages.length : pack.page_count ?? packPages.length;

    return (
      <DropdownFlex direction='column' mb='3px'>
        <DropdownFlex
          alignItems='center'
          gap='8px'
          px='8px'
          py='8px'
          borderRadius={radius.control}
          bg={isActivePack ? accent.subtle : 'transparent'}
          _hover={{ bg: isActivePack ? accent.subtle : neutral.surfaceHover }}
        >
          <DropdownFlex
            as='button'
            type='button'
            aria-label={isExpanded ? 'Collapse pages' : 'Expand pages'}
            aria-expanded={isExpanded}
            w='28px'
            h='28px'
            flexShrink={0}
            alignItems='center'
            justifyContent='center'
            borderRadius={radius.control}
            border='none'
            bg='transparent'
            color={text.secondary}
            cursor='pointer'
            _hover={{ bg: neutral.surfaceHover, color: text.primary }}
            onClick={(event: ReactMouseEvent) => onToggleExpand(pack, event)}
          >
            <ExpandChevronIcon expanded={isExpanded} />
          </DropdownFlex>
          <DropdownFlex
            flex='1'
            minW={0}
            alignItems='center'
            gap='10px'
            cursor='pointer'
            onClick={(event: ReactMouseEvent) => {
              event.stopPropagation();
              onDashboardOpen(pack.id);
            }}
          >
            <DropdownFlex
              w='32px'
              h='32px'
              flexShrink={0}
              alignItems='center'
              justifyContent='center'
              borderRadius={radius.control}
              bg={tint.bg}
              color={tint.fg}
              fontFamily={font.body}
              fontSize='11px'
              fontWeight='700'
            >
              {initialsOf(pack.title)}
            </DropdownFlex>
            <DropdownFlex direction='column' flex='1' minW={0} gap='2px'>
              <DropdownBox
                color={isActivePack ? accent.solid : text.primary}
                fontFamily={font.body}
                fontSize='14px'
                fontWeight={isActivePack ? '600' : '500'}
                noOfLines={1}
              >
                {pack.title}
              </DropdownBox>
              <DropdownBox color={text.secondary} fontSize='12px' noOfLines={1}>
                {pageCount} page{pageCount === 1 ? '' : 's'}
                {isLoadingPages ? ' · Loading…' : ''}
              </DropdownBox>
            </DropdownFlex>
          </DropdownFlex>
        </DropdownFlex>
        {isExpanded &&
          (packPages.length > 0
            ? packPages.map((page) => (
                <DashboardNavigationPageRow
                  key={page.id}
                  page={page}
                  indented
                  isActive={previewPageId === page.id}
                  onHover={onPageHover}
                  onPageClick={onPageClick}
                />
              ))
            : !isLoadingPages && (
                <DropdownBox
                  pl='46px'
                  pr='10px'
                  py='6px'
                  color={text.muted}
                  fontSize='12px'
                  fontFamily={font.body}
                >
                  No pages in this dashboard
                </DropdownBox>
              ))}
      </DropdownFlex>
    );
  },
);

DashboardNavigationPackRow.displayName = 'DashboardNavigationPackRow';

export default DashboardNavigationPackRow;
